import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import ModalPopUp from "@/Components/common/ModalPopUp";
import {
  terminatedByList,
  terminatedReasonsList,
} from "@/Constants/filterConstants";
import { fetchFiltersData } from "@/reducers/clientscandidates";
import {
  deSelectAllCandidate,
  fetchTerminatedTimesheets,
  reStateFilters,
  setTerminatedTimesheetsLoading,
  showFilterModal,
  updateFilters,
  updatePagination,
} from "@/reducers/timesheets/timesheetterminated";

import CalendarFilter from "../../../ListingFilters/CalendarFilter";
import ComplianceStatusFilter from "../../../ListingFilters/ComplianceStatusFilter";
import DropDownFilter from "../../../ListingFilters/DropDownFilter";
import FilterModalBottomAction from "../../../ListingFilters/FilterModalBottomAction";
import { FilterModalTop } from "../../../ListingFilters/FilterModalTop";
import MilesFilter from "../../../ListingFilters/MilesFilter";
import SessionTypeFilter from "../../../ListingFilters/SessionTypeFilter";
import TextFieldFilter from "../../../ListingFilters/TextFieldFilter";

/**
 * Renders the FilterModal component with various filter options and actions.
 *
 * @return {JSX.Element} The rendered FilterModal component
 */
export const FilterModal = () => {
  const dispatch = useDispatch();
  const { filters, showFilters, pagination } = useSelector(
    (state) => state.clientTimesheetTerminated,
  );
  const { filterLists } = useSelector((state) => state.clientsCandidates);
  /**
   * Function to handle the closing of the modal.
   *
   * @param {boolean} fromBtn - indicates if the function was called from a button
   * @return {void}
   */
  const handleClose = (fromBtn = false) => {
    if (fromBtn) {
      dispatch(reStateFilters());
    }
    dispatch(showFilterModal(false));
  };
  useEffect(() => {
    dispatch(fetchFiltersData());
  }, []);
  /**
   * This function handles the apply action by dispatching various actions and closing a modal.
   */
  const handleApply = () => {
    dispatch(setTerminatedTimesheetsLoading(true));
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(deSelectAllCandidate());
    dispatch(fetchTerminatedTimesheets(filters));
    handleClose();
  };
  /**
   * Function to handle reset.
   */
  const handleReset = () => {
    dispatch(updateFilters({}));
    dispatch(setTerminatedTimesheetsLoading(true));
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(deSelectAllCandidate());
    dispatch(
      fetchTerminatedTimesheets({
        search: filters.search,
      }),
    );
    handleClose();
  };
  return (
    <ModalPopUp
      show={showFilters}
      panelClass="max-w-[774px]"
      onClose={() => {
        handleClose(true);
      }}
    >
      <div className="grid gap-6">
        <div className="grid gap-[18px]">
          <div className="grix gap-4">
            <FilterModalTop
              handleClose={() => {
                handleClose(true);
              }}
            />
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-[18px] sm:grid-cols-2">
            <SessionTypeFilter
              active={filters.sessionType}
              handleClick={(value) => {
                dispatch(updateFilters({ sessionType: value }));
              }}
            />
            <DropDownFilter
              options={
                terminatedByList && terminatedByList?.length > 0
                  ? terminatedByList
                  : []
              } // this is the list of terminated by
              id="terminatedByType"
              label="Terminated by (Type)"
              placeholder="Select Terminated By"
              value={filters.terminatedBy} // selected terminated by
              handleChange={(value) => {
                dispatch(updateFilters({ terminatedBy: value }));
              }} // function to handle change of terminated by
              isMultiple={true}
            />
            <DropDownFilter
              options={
                terminatedReasonsList && terminatedReasonsList?.length > 0
                  ? terminatedReasonsList
                  : []
              } // this is the list of terminated reason
              id="terminatedReason"
              label="Terminated Reason"
              placeholder="Select Terminated Reason"
              value={filters.terminatedReason} // selected terminated reason
              handleChange={(value) => {
                dispatch(updateFilters({ terminatedReason: value }));
              }} // function to handle change of terminated by
              isMultiple={true}
            />
            <TextFieldFilter
              handleKeyUp={(e) => {
                if (e.keyCode === 13) {
                  handleApply();
                }
              }}
              handleChange={(value) => {
                dispatch(updateFilters({ contractReferenceNo: value }));
              }}
              label="Contract Reference No."
              value={filters.contractReferenceNo}
              filterId="contractReferenceNo"
              placeholder="Enter Reference No."
            />
            <DropDownFilter
              options={
                filterLists && filterLists?.allCandidates?.length > 0
                  ? filterLists?.allCandidates
                  : []
              } // this is the list of candidates
              id="candidateId"
              label="Candidate"
              placeholder="Select Candidate"
              value={filters.candidateId} // selected candidate
              handleChange={(value) => {
                dispatch(updateFilters({ candidateId: value }));
              }} // function to handle change of candidate
              isMultiple={true}
            />
            <TextFieldFilter
              handleKeyUp={(e) => {
                if (e.keyCode === 13) {
                  handleApply();
                }
              }}
              handleChange={(value) => {
                dispatch(updateFilters({ candidateReferenceNo: value }));
              }}
              label="Candidate Reference No."
              value={filters.candidateReferenceNo}
              filterId="candidateReferenceNo"
              placeholder="Enter Reference No."
            />
            <TextFieldFilter
              handleKeyUp={(e) => {
                if (e.keyCode === 13) {
                  handleApply();
                }
              }}
              handleChange={(value) => {
                dispatch(updateFilters({ candidateLicenseNo: value }));
              }}
              label="Prof. Registration No."
              value={filters.candidateLicenseNo}
              filterId="candidateLicenseNo"
              placeholder="GMC/NMC/GPhC."
            />
            <ComplianceStatusFilter
              value={filters.complianceStatus}
              handleChange={(value) => {
                dispatch(updateFilters({ complianceStatus: value }));
              }}
            />
            <DropDownFilter
              options={
                filterLists && filterLists?.allRoles?.length > 0
                  ? filterLists?.allRoles
                  : []
              } // this is the list of roles
              id="candidateRole"
              label="Role"
              placeholder="Select Role"
              value={filters.candidateRole} // selected role
              handleChange={(value) => {
                dispatch(updateFilters({ candidateRole: value }));
              }} // function to handle change of role
            />
            <MilesFilter
              value={filters.miles}
              handleChange={(value) => {
                dispatch(updateFilters({ miles: value }));
              }}
            />
            <DropDownFilter
              options={
                filterLists && filterLists?.allUsers?.length > 0
                  ? filterLists?.allUsers
                  : []
              } // this is the list of roles
              id="Terminated By"
              label="Terminated by (Name)"
              // isMultiple="false"
              placeholder="Select Terminated by"
              value={filters.actionedById} // selected role
              handleChange={(value, type) => {
                dispatch(
                  updateFilters({ actionedById: value, actionedByType: type }),
                );
              }} // function to handle change of role
            />
          </div>
          <div className="_filter_calendar">
            <CalendarFilter
              label="Date Worked (From - To)"
              dateFrom={filters.workedDateFrom}
              dateTo={filters.workedDateTo}
              handleChange={(value) => {
                dispatch(
                  updateFilters({
                    workedDateFrom: value.dateFrom,
                    workedDateTo: value.dateTo,
                  }),
                );
              }}
            />
          </div>
          <div className="_filter_calendar">
            <CalendarFilter
              label="Shift Date (From - To)"
              dateFrom={filters.contractDateFrom}
              dateTo={filters.contractDateTo}
              handleChange={(value) => {
                dispatch(
                  updateFilters({
                    contractDateFrom: value.dateFrom,
                    contractDateTo: value.dateTo,
                  }),
                );
              }}
            />
          </div>
        </div>
        <FilterModalBottomAction
          applyFunc={handleApply}
          resetFunc={handleReset}
        />
      </div>
    </ModalPopUp>
  );
};
